/**
 * Compose a friendly, professional pre-filled message from Best Match
 * quiz answers. Used when a learner taps "Message" on a matched
 * instructor — removes the blank-page freeze that kills lead conversion.
 *
 * Tone rules:
 *   - Warm, professional, human. Never salesy.
 *   - Editable before send (so we use natural language, not bullet points).
 *   - Always degrade gracefully if a field is missing.
 *   - Mention the quiz so the instructor knows context up-front.
 */

// Confidence → natural phrase describing the learner.
const CONFIDENCE_PHRASE = {
  nervous: "nervous learner who'd value a calm, patient instructor",
  building: "learner building my confidence on the road",
  confident: "confident learner working towards my test",
};

// Vehicle → "manual lessons" / "automatic lessons" / null
const VEHICLE_PHRASE = {
  manual: "manual lessons",
  automatic: "automatic lessons",
  unsure: null, // we'll say "still deciding between manual and automatic"
};

// Style → optional descriptor blended into the second sentence.
const STYLE_PHRASE = {
  patient: "a calm, step-by-step approach",
  structured: "a clear, structured plan towards the test",
  fast_track: "an intensive or fast-track course",
  calm: "a calm, reassuring teaching style",
};

// Extras → short human labels appended in a natural list.
const EXTRA_LABEL = {
  neurodivergent: "neurodivergent-friendly support",
  anxiety: "anxiety-aware coaching",
  female_instructor: "a female instructor",
  male_instructor: "a male instructor",
  lgbtq: "an LGBTQ+ friendly instructor",
  disability: "a disability-confident instructor",
  motorway: "motorway lessons",
  night: "night driving practice",
  pass_plus: "Pass Plus",
  ev: "EV / hybrid familiarisation",
  towing: "towing or trailer tuition",
  intensive: "an intensive course",
};

// Join an array as a natural English list: "A, B and C".
function joinNatural(items) {
  const xs = (items || []).filter(Boolean);
  if (xs.length === 0) return "";
  if (xs.length === 1) return xs[0];
  if (xs.length === 2) return `${xs[0]} and ${xs[1]}`;
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/**
 * Build the pre-filled message. All inputs optional.
 *
 * @param {Object} args
 * @param {string} [args.instructorName]  First name of instructor (for greeting)
 * @param {string} [args.confidence]      "nervous" | "building" | "confident"
 * @param {string} [args.vehicle]         "manual" | "automatic" | "unsure"
 * @param {string} [args.style]           "patient" | "structured" | "fast_track" | "calm"
 * @param {string[]} [args.extras]        Wizard extras values
 * @param {string} [args.postcode]        e.g. "M1 1AA"
 * @param {string} [args.preferred_times] Free-text e.g. "weekday evenings"
 * @returns {string} Multi-line message ready for the textarea.
 */
export function composeQuizMessage({
  instructorName,
  confidence,
  vehicle,
  style,
  extras = [],
  postcode,
  preferred_times,
} = {}) {
  // Greeting — first name only if it looks like a real word (avoid "Hi user_id...")
  const firstName =
    instructorName && /^[A-Za-z][A-Za-z' -]*$/.test(instructorName)
      ? instructorName.split(" ")[0]
      : null;
  const greeting = firstName ? `Hi ${firstName},` : "Hi,";

  // Sentence 1 — context
  const intro =
    "I completed the Best Match quiz on National Driving Guild and you came up as a strong fit for me.";

  // Sentence 2 — about me
  const confidenceBit = CONFIDENCE_PHRASE[confidence] || "learner";
  const styleBit = STYLE_PHRASE[style];
  let aboutMe = `I'm a ${confidenceBit}`;
  if (styleBit) aboutMe += `, and I'd really value ${styleBit}`;
  aboutMe += ".";

  // Sentence 3 — what I'm looking for (vehicle + extras combined naturally)
  const vehicleBit =
    vehicle === "unsure"
      ? "I'm still deciding between manual and automatic"
      : VEHICLE_PHRASE[vehicle]
      ? `I'm interested in ${VEHICLE_PHRASE[vehicle]}`
      : null;

  const extraLabels = (extras || [])
    .map((e) => EXTRA_LABEL[e])
    .filter(Boolean);
  const extrasBit = extraLabels.length
    ? `${vehicleBit ? "and would also value" : "I'd value"} ${joinNatural(extraLabels)}`
    : null;

  let lookingFor = "";
  if (vehicleBit && extrasBit) {
    lookingFor = `${vehicleBit}, ${extrasBit}.`;
  } else if (vehicleBit) {
    lookingFor = `${vehicleBit}.`;
  } else if (extrasBit) {
    lookingFor = `${extrasBit.charAt(0).toUpperCase()}${extrasBit.slice(1)}.`;
  }

  // Sentence 4 — area / times if provided
  const locationParts = [];
  if (postcode) locationParts.push(`I'm based around ${postcode}`);
  if (preferred_times) {
    locationParts.push(
      locationParts.length
        ? `and ${preferred_times.toLowerCase().startsWith("weekday") || preferred_times.toLowerCase().startsWith("weekend") || preferred_times.toLowerCase().startsWith("morning") || preferred_times.toLowerCase().startsWith("evening") || preferred_times.toLowerCase().startsWith("saturday") || preferred_times.toLowerCase().startsWith("sunday") ? "tend to prefer" : "prefer"} ${preferred_times}`
        : `I tend to prefer ${preferred_times}`
    );
  }
  const locationLine = locationParts.length ? `${locationParts.join(", ")}.` : "";

  // Sign-off
  const closing = "Could you let me know your availability and next steps?";
  const signoff = "Thanks!";

  // Assemble — blank lines between paragraphs, skipping any empty piece.
  const paragraphs = [
    greeting,
    intro,
    [aboutMe, lookingFor].filter(Boolean).join(" "),
    locationLine,
    closing,
    signoff,
  ].filter(Boolean);

  return paragraphs.join("\n\n");
}

export default composeQuizMessage;
